import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { Building2, Users, Mail, Phone, MapPin } from 'lucide-react';

const EDITION_COLORS = {
  standard: 'bg-slate-500',
  professional: 'bg-blue-500',
  enterprise: 'bg-purple-500',
};

export const CustomerViewDialog = ({ customer, open, onOpenChange }) => {
  if (!customer) return null;

  const copyLicenseKey = () => {
    if (customer.license?.license_key) {
      navigator.clipboard.writeText(customer.license.license_key);
      toast.success('License key copied to clipboard!');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            {customer.company_name}
          </DialogTitle>
          <DialogDescription>
            Site ID: <code className="bg-muted px-2 py-1 rounded">{customer.site_id}</code>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Status & Edition */}
          <div className="flex gap-2">
            <Badge variant={customer.status === 'active' ? 'default' : 'destructive'}>
              {customer.status}
            </Badge>
            <Badge className={`${EDITION_COLORS[customer.edition]} text-white`}>
              {customer.edition}
            </Badge>
            <Badge variant={customer.license?.is_activated ? 'default' : 'secondary'}>
              {customer.license?.is_activated ? '✓ Activated' : '⏳ Pending Activation'}
            </Badge>
          </div>

          {/* License Key Box */}
          <Card className={customer.license?.is_activated ? 'border-green-500/30 bg-green-500/5' : 'border-amber-500/30 bg-amber-500/5'}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">License Key</p>
                  <code className="text-lg font-mono font-bold tracking-wider">
                    {customer.license?.license_key || 'N/A'}
                  </code>
                </div>
                <Button variant="outline" size="sm" onClick={copyLicenseKey}>
                  Copy
                </Button>
              </div>
              {customer.license?.is_activated && customer.license?.activation_date && (
                <p className="text-xs text-muted-foreground mt-2">
                  Activated on {new Date(customer.license.activation_date).toLocaleString()}
                  {customer.license.activation_ip && ` from IP: ${customer.license.activation_ip}`}
                </p>
              )}
            </CardContent>
          </Card>

          {/* License Info */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">License Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Creator Licenses:</span>
                <span className="font-medium">{customer.license?.creator_licenses || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Viewer Licenses:</span>
                <span className="font-medium">{customer.license?.viewer_licenses || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Start Date:</span>
                <span>{customer.license?.start_date ? new Date(customer.license.start_date).toLocaleDateString() : '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">End Date:</span>
                <span className={customer.license?.days_remaining <= 30 ? 'text-amber-500 font-medium' : ''}>
                  {customer.license?.end_date ? new Date(customer.license.end_date).toLocaleDateString() : '-'}
                  {customer.license?.days_remaining !== undefined && ` (${customer.license.days_remaining} days)`}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Contact Person</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                <span>{customer.contact_person?.name}</span>
                {customer.contact_person?.designation && (
                  <span className="text-muted-foreground">({customer.contact_person.designation})</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span>{customer.contact_person?.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-muted-foreground" />
                <span>{customer.contact_person?.phone}</span>
              </div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Address</CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                <div>
                  <p>{customer.address?.address_line1}</p>
                  {customer.address?.address_line2 && <p>{customer.address.address_line2}</p>}
                  <p>{customer.address?.city}, {customer.address?.state} - {customer.address?.pincode}</p>
                  <p>{customer.address?.country}</p>
                </div>
              </div>
              {(customer.gst_number || customer.pan_number) && (
                <div className="mt-3 pt-3 border-t space-y-1">
                  {customer.gst_number && <p><span className="text-muted-foreground">GST:</span> {customer.gst_number}</p>}
                  {customer.pan_number && <p><span className="text-muted-foreground">PAN:</span> {customer.pan_number}</p>}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};
